import React, { useState, useEffect, useRef, useCallback } from 'react';
import FadeLoader from 'react-spinners/FadeLoader';
import { getPacksApi } from '../../util/api_calls/ProjectApiCalls';
import './CardsList.css';

/**
 * CardsList
 *
 * Data flow:
 *   On every mount (page load / refresh) → GET /packs/dc/all
 *   Response: {
 *     success,
 *     data: {
 *       packs: [ { id, packName, packImageUrl } ],
 *       cards: [ { id, firstName, lastName, fullName, originalHeadshotUrl,
 *                  filters: [...], packIds: [1, 2] } ]
 *     }
 *   }
 *
 * Cards live at the top level of data — a card can belong to multiple packs
 * via its packIds array. No second API call is made.
 *
 * Dropdown logic:
 *   • If ANY card has a non-empty `filters` array  → dropdown shows unique filters,
 *     a card appears when its filters array includes the selected value.
 *   • Otherwise                                    → dropdown shows pack names,
 *     a card appears when its packIds includes the selected pack id.
 *   • "All" is always the first / default option   → shows every card.
 *
 * Card modal: left/right arrows + keyboard ← → + touch swipe.
 */
const CardsList = ({ comp, isUserPopupShown, onGoToUnlock }) => {

    // ── Raw data from API ─────────────────────────────────────────────────
    const [allCards,     setAllCards]     = useState([]);   // flat list, all packs
    const [dropdownOpts, setDropdownOpts] = useState([]);   // [{ value, label }]
    const [useFilters,   setUseFilters]   = useState(false);// true = filter mode
    const [loading,      setLoading]      = useState(false);
    const [error,        setError]        = useState(false);

    // ── Selection ─────────────────────────────────────────────────────────
    const [selected,   setSelected]   = useState('all');  // 'all' | filter | packId

    // ── Derived: cards currently visible ─────────────────────────────────
    const visibleCards = (() => {
        if (selected === 'all') return allCards;
        if (useFilters) {
            // Filter mode: card visible if its filters array contains the selection
            return allCards.filter(c =>
                Array.isArray(c.filters) && c.filters.includes(selected)
            );
        }
        // Pack mode: card visible if its packIds array includes the selected pack id
        // selected is stored as a string in the <select> value, so coerce both sides
        return allCards.filter(c =>
            Array.isArray(c.packIds) &&
            c.packIds.some(pid => String(pid) === String(selected))
        );
    })();

    // ── Modal state ───────────────────────────────────────────────────────
    const [modalIndex,  setModalIndex]  = useState(null);
    const [selectedMsg, setSelectedMsg] = useState(null);

    const touchStartX = useRef(null);

    // ── Competition colours ───────────────────────────────────────────────
    const primaryColor = comp ? (comp.primaryColor   || '#000') : '#000';
    const buttonBg     = comp ? (comp.buttonColor    || '#000') : '#000';
    const borderColor  = comp ? (comp.borderColor    || '#000') : '#000';
    const buttonColor  = comp ? (comp.secondaryColor || '#ffffff') : '#ffffff';

    // ── Welcome popup ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!isUserPopupShown) setSelectedMsg(1);
    }, []);

    // ── Fetch all packs + cards on every mount ────────────────────────────
    // No dependency array cache — always re-fetches so a page refresh always
    // pulls the latest data from the server.
    useEffect(() => {
        const token = localStorage.getItem('token');
        setLoading(true);
        setError(false);

        getPacksApi(
            { token },
            (response) => {
                setLoading(false);

                // New response shape: data.packs + data.cards (separate arrays)
                // A card can belong to multiple packs via card.packIds: [1, 2]
                const responseData = (response && response.data) ? response.data : {};
                const packs = Array.isArray(responseData.packs) ? responseData.packs : [];
                const cards = Array.isArray(responseData.cards) ? responseData.cards : [];

                // Build a packId → packName lookup for pack-mode dropdown labels
                const packMap = {};
                packs.forEach(p => {
                    packMap[p.id] = p.packName || `Pack ${p.id}`;
                });

                setAllCards(cards);

                // Decide dropdown mode ─────────────────────────────────────
                // Collect all unique, non-empty filter values across all cards
                const filterSet = new Set();
                cards.forEach(c => {
                    if (Array.isArray(c.filters)) {
                        c.filters.forEach(f => {
                            if (f && f.trim()) filterSet.add(f.trim());
                        });
                    }
                });

                if (filterSet.size > 0) {
                    // Filter mode — one option per unique filter value
                    setUseFilters(true);
                    setDropdownOpts(
                        Array.from(filterSet).sort().map(f => ({ value: f, label: f }))
                    );
                } else {
                    // Pack mode — one option per pack (preserve API order)
                    setUseFilters(false);
                    setDropdownOpts(
                        packs.map(p => ({
                            value: String(p.id),
                            label: packMap[p.id],
                        }))
                    );
                }

                // Default to "All"
                setSelected('all');
            },
            () => {
                setLoading(false);
                setError(true);
            }
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Empty deps = runs once per mount. Because this component is unmounted
    // and remounted on every navigation to /cards, every page visit triggers
    // a fresh fetch.

    // ── Dropdown change ───────────────────────────────────────────────────
    const handleChange = (e) => {
        setSelected(e.target.value);
        setModalIndex(null); // close any open modal when filter changes
    };

    // ── Modal helpers ─────────────────────────────────────────────────────
    const openModal  = (index) => setModalIndex(index);
    const closeModal = ()      => setModalIndex(null);

    const prevCard = useCallback(() => {
        setModalIndex(i => (i - 1 + visibleCards.length) % visibleCards.length);
    }, [visibleCards.length]);

    const nextCard = useCallback(() => {
        setModalIndex(i => (i + 1) % visibleCards.length);
    }, [visibleCards.length]);

    // Keyboard navigation on modal
    useEffect(() => {
        if (modalIndex === null) return;
        const handler = (e) => {
            if (e.key === 'ArrowLeft')  prevCard();
            if (e.key === 'ArrowRight') nextCard();
            if (e.key === 'Escape')     closeModal();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [modalIndex, prevCard, nextCard]);

    // Touch swipe
    const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const onTouchEnd   = (e) => {
        if (touchStartX.current === null) return;
        const delta = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 40) delta > 0 ? nextCard() : prevCard();
        touchStartX.current = null;
    };

    const stopProp = (e) => e.stopPropagation();

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="sticker-book desc-font" style={{ color: primaryColor }}>

            {/* ── Dropdown ── */}
            <div className="sb-dropdown-row">
                {loading ? (
                    <span className="sb-loading">Loading…</span>
                ) : (
                    <select
                        className="form-control round-select"
                        value={selected}
                        onChange={handleChange}
                        style={{ border: `1px solid ${borderColor}` }}
                        disabled={dropdownOpts.length === 0}
                    >
                        {/* "All" is always the first and default option */}
                        <option value="all">All</option>

                        {dropdownOpts.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* ── Cards grid ── */}
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <FadeLoader size={80} color={buttonBg} loading={true} />
                </div>
            ) : error ? (
                <div className="sb-empty">Could not load cards. Please try again.</div>
            ) : visibleCards.length === 0 ? (
                <div className="sb-empty">No cards yet.</div>
            ) : (
                <div className="sb-grid">
                    {visibleCards.map((card, index) => (
                        <div
                            key={`card-${card.id}-${index}`}
                            className="sb-grid-item"
                            onClick={() => openModal(index)}
                        >
                            <img
                                src={card.originalHeadshotUrl || card.packImageUrl || card.url}
                                alt={card.fullName || `${card.firstName || ''} ${card.lastName || ''}`.trim() || `Card ${index + 1}`}
                                className="sb-card-img"
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* ── Bottom link ── */}
            <button
                className={`unlock-link-btn cu-btn`}
                style={{ backgroundColor: buttonBg, color: buttonColor }}
                onClick={onGoToUnlock}
            >
                UNLOCK NOW
            </button>
            {/* <div className="card-bottom">
                <p
                    className="card-info-link"
                    onClick={() => setSelectedMsg(2)}
                >
                    How do I unlock more cards?
                </p>
            </div>*/}

            {/* ══════════════════════════════════════════════════════════
                Card detail modal — arrows + swipe
            ══════════════════════════════════════════════════════════ */}
            {modalIndex !== null && visibleCards.length > 0 && (
                <div className="sb-modal-backdrop" onClick={closeModal}>
                    <div
                        className="sb-card-modal"
                        onTouchStart={onTouchStart}
                        onTouchEnd={onTouchEnd}
                        onClick={stopProp}
                    >
                        {/* Counter */}
                        {visibleCards.length > 1 && (
                            <div className="sb-card-modal__counter">
                                {modalIndex + 1} / {visibleCards.length}
                            </div>
                        )}

                        {/* Close */}
                        <button className="sb-modal-close" onClick={closeModal} aria-label="Close">
                            &times;
                        </button>

                        {/* Prev */}
                        {visibleCards.length > 1 && (
                            <button
                                className="sb-modal-arrow sb-modal-arrow--left"
                                onClick={(e) => { e.stopPropagation(); prevCard(); }}
                                aria-label="Previous card"
                            >
                                &#8249;
                            </button>
                        )}

                        {/* Card */}
                        {(() => {
                            const card = visibleCards[modalIndex];
                            return (
                                <div
                                    className="sb-card-modal__inner"
                                    key={`modal-${card.id}`}
                                >
                                    {(card.originalHeadshotUrl || card.packImageUrl || card.url) && (
                                        <img
                                            src={card.originalHeadshotUrl || card.packImageUrl || card.url}
                                            alt={card.fullName || `${card.firstName || ''} ${card.lastName || ''}`.trim()}
                                            className="sb-card-modal__image"
                                        />
                                    )}
                                </div>
                            );
                        })()}

                        {/* Next */}
                        {visibleCards.length > 1 && (
                            <button
                                className="sb-modal-arrow sb-modal-arrow--right"
                                onClick={(e) => { e.stopPropagation(); nextCard(); }}
                                aria-label="Next card"
                            >
                                &#8250;
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════
                Info / message modals
            ══════════════════════════════════════════════════════════ */}
            {/* {selectedMsg && (
                <div className="sb-modal-backdrop" onClick={() => setSelectedMsg(null)}>
                    <div className="sb-msg-modal desc-font" onClick={stopProp}>
                        <button
                            className="sb-msg-close"
                            onClick={() => setSelectedMsg(null)}
                            aria-label="Close"
                        >
                            &times;
                        </button>

                        {selectedMsg === 1 && (
                            <div>
                                <h2><b>Welcome to the<br />Ninja A-League Women Heroes Collection</b></h2>
                                <p>You're in!</p>
                                <p>Your collection starts now with 7 complimentary Ninja A-League Heroes cards unlocked and ready to view.</p>
                                <p>More Heroes and exclusive drops are coming soon.</p>
                            </div>
                        )}

                        {selectedMsg === 2 && (
                            <div>
                                <h2><b>How to Collect</b></h2>
                                <p>There are plenty of ways to grow your Ninja A-League Women Heroes Collection.</p>
                                <p>The more you engage with the A-Leagues, the more Heroes you can unlock.</p>
                                <h3>Scan at Matches</h3>
                                <p>Across A-League matches, scan the QR code to unlock exclusive card packs.</p>
                                <h3>Subscribe &amp; Unlock Weekly</h3>
                                <p>Subscribe to the A-Leagues newsletter for the chance to unlock 2 new Hero cards every week.</p>
                                <h3>Watch Dub Zone</h3>
                                <p>Tune into Dub Zone during the season for chances to unlock new Heroes.</p>
                                <h3>Download the App</h3>
                                <p>Download the A-Leagues app and turn on push notifications to unlock exclusive cards.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}*/}
        </div>
    );
};

export default CardsList;
